import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  LinearProgress,
  Select,
  FormControl,
  Checkbox,
  InputLabel,
  InputAdornment,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Domain as DomainIcon,
  Search as SearchIcon,
  AutoAwesome as AIIcon,
  Category as CategoryIcon,
  PendingActions as PendingIcon,
  VerifiedUser as ValidatedIcon,
  Block as RejectedIcon,
  Public as GlobalIcon
} from '@mui/icons-material';
import api from '../../../../services/api';
import PlatformDialog from '../../../molecules/PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../../../molecules/PlatformDialog/PlatformDialogTitle';

const TIER_COLORS = {
  1: 'success',
  2: 'warning',
  3: 'default'
};

const TIER_LABELS = {
  1: 'T1 - Alto',
  2: 'T2 - Medio',
  3: 'T3 - Baixo'
};

const STATUS_COLORS = {
  pending: 'warning',
  validated: 'success',
  rejected: 'error'
};

const STATUS_LABELS = {
  pending: 'Pendente',
  validated: 'Validado',
  rejected: 'Rejeitado'
};

const PLATFORM_LABELS = {
  website: 'Website',
  youtube: 'YouTube',
  instagram: 'Instagram',
  tiktok: 'TikTok',
  twitter: 'Twitter/X',
  facebook: 'Facebook',
  linkedin: 'LinkedIn'
};

const humanizeHandle = (value) => {
  if (!value) return '';
  return value
    .toString()
    .replace(/^@/, '')
    .replace(/[_.-]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
};

const getDisplaySourceName = (source) => {
  const rawName = source?.siteName || '';

  if (!rawName) {
    return source?.username ? humanizeHandle(source.username) : '-';
  }

  const cleaned = rawName.replace(/^@/, '').trim();
  const looksLikeHandle = /^[a-z0-9_.-]+$/i.test(cleaned) && cleaned.length > 0;

  if (looksLikeHandle && source?.platform && source.platform !== 'website') {
    return humanizeHandle(cleaned);
  }

  return rawName;
};

const isHandleLike = (value) => {
  if (!value) return false;
  const cleaned = value.toString().replace(/^@/, '').trim();
  return /^[a-z0-9_.-]+$/i.test(cleaned) && cleaned.length > 0;
};

const GlobalSourceManagement = () => {
  const [loading, setLoading] = useState(true);
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Categorias carregadas da API (fonte unica da verdade: backend/config/categories.js)
  const [availableCategories, setAvailableCategories] = useState([]);

  // Filtros
  const [statusFilter, setStatusFilter] = useState('pending');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [dialogOpen, setDialogOpen] = useState(false);
  const [validateDialogOpen, setValidateDialogOpen] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const [selectedSources, setSelectedSources] = useState([]);

  // Form
  const [formData, setFormData] = useState({
    id: null,
    domain: '',
    siteName: '',
    platform: 'website',
    categories: [],
    sizeTier: 3,
    segmentTier: '',
    defaultTier: 3,
    subscribersEstimate: 0,
    notes: '',
    status: 'pending'
  });

  // Carregar categorias da API (uma vez)
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await api.get('/brands/categories');
        if (res.data.success) {
          setAvailableCategories(res.data.data);
        }
      } catch (err) {
        console.error('Erro ao carregar categorias:', err);
      }
    };
    fetchCategories();
  }, []);

  // Carregar dados
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [sourcesRes, statsRes] = await Promise.all([
        api.get('/global-sources', {
          params: {
            status: statusFilter !== 'all' ? statusFilter : undefined,
            category: categoryFilter || undefined,
            platform: platformFilter || undefined,
            search: searchTerm || undefined,
            limit: 100
          }
        }),
        api.get('/global-sources/stats')
      ]);

      setSources(sourcesRes.data.data || []);
      setStats(statsRes.data.data);
    } catch (err) {
      setError('Erro ao carregar dados: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter, platformFilter, searchTerm]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (newValue === 0) setStatusFilter('pending');
    else if (newValue === 1) setStatusFilter('validated');
    else if (newValue === 2) setStatusFilter('rejected');
    else setStatusFilter('all');
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      id: null,
      domain: '',
      siteName: '',
      platform: 'website',
      categories: [],
      sizeTier: 3,
      segmentTier: '',
      defaultTier: 3,
      subscribersEstimate: 0,
      notes: '',
      status: 'pending'
    });
    setSelectedSource(null);
    setDialogOpen(true);
  };

  const handleOpenValidateDialog = (source) => {
    setSelectedSource(source);
    setFormData({
      id: source._id,
      domain: source.domain,
      siteName: source.siteName || '',
      platform: source.platform || 'website',
      categories: source.categories || [],
      sizeTier: source.sizeTier || source.defaultTier || 3,
      segmentTier: source.segmentTier || '',
      defaultTier: source.defaultTier || 3,
      subscribersEstimate: source.subscribersEstimate || 0,
      notes: source.notes || '',
      status: source.status
    });
    setValidateDialogOpen(true);
  };

  const handleCreateSource = async () => {
    try {
      setError('');
      await api.post('/global-sources', formData);
      setSuccess('Fonte criada com sucesso!');
      setDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar fonte');
    }
  };

  const handleValidateSource = async () => {
    try {
      setError('');
      await api.post(`/global-sources/${selectedSource._id}/validate`, formData);
      setSuccess('Fonte validada com sucesso!');
      setValidateDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao validar fonte');
    }
  };

  const handleRejectSource = async (source, reason = '') => {
    try {
      setError('');
      await api.post(`/global-sources/${source._id}/reject`, { reason });
      setSuccess('Fonte rejeitada');
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao rejeitar fonte');
    }
  };

  const handleBulkValidate = async () => {
    if (selectedSources.length === 0) return;

    try {
      setError('');
      await api.post('/global-sources/bulk-validate', {
        sourceIds: selectedSources
      });
      setSuccess(`${selectedSources.length} fontes validadas`);
      setSelectedSources([]);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao validar em massa');
    }
  };

  const handleCategorizeWithAI = async () => {
    if (selectedSources.length === 0) {
      setError('Selecione ao menos uma fonte');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await api.post('/global-sources/categorize-ai', {
        sourceIds: selectedSources
      });
      setSuccess(`${res.data.data.updated} fontes categorizadas com IA`);
      setSelectedSources([]);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao categorizar com IA');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSyncFromArticles = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.post('/global-sources/sync-from-articles');
      setSuccess(res.data.message);
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao sincronizar artigos');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectSource = (sourceId) => {
    setSelectedSources(prev =>
      prev.includes(sourceId)
        ? prev.filter(id => id !== sourceId)
        : [...prev, sourceId]
    );
  };

  const handleSelectAll = () => {
    if (selectedSources.length === sources.length) {
      setSelectedSources([]);
    } else {
      setSelectedSources(sources.map(s => s._id));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <GlobalIcon sx={{ fontSize: 32, color: 'primary.main', mr: 1.5 }} />
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Base Global de Fontes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Gerenciamento centralizado de fontes de notícias
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Estatisticas */}
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <PendingIcon color="warning" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{stats.pending}</Typography>
                <Typography variant="body2" color="text.secondary">Pendentes</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <ValidatedIcon color="success" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{stats.validated}</Typography>
                <Typography variant="body2" color="text.secondary">Validados</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <RejectedIcon color="error" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{stats.rejected}</Typography>
                <Typography variant="body2" color="text.secondary">Rejeitados</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <DomainIcon color="primary" sx={{ fontSize: 32 }} />
                <Typography variant="h4">{stats.total}</Typography>
                <Typography variant="body2" color="text.secondary">Total</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs e Filtros */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label={`Pendentes (${stats?.pending || 0})`} />
            <Tab label={`Validados (${stats?.validated || 0})`} />
            <Tab label={`Rejeitados (${stats?.rejected || 0})`} />
            <Tab label="Todos" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar dominio..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              label="Categoria"
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {availableCategories.map(cat => (
                <MenuItem key={cat.value} value={cat.value}>{cat.value}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Plataforma</InputLabel>
            <Select
              value={platformFilter}
              label="Plataforma"
              onChange={(e) => setPlatformFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
          >
            Atualizar
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
          >
            Nova Fonte
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<RefreshIcon />}
            onClick={handleSyncFromArticles}
            disabled={loading}
          >
            Sincronizar Todas as Fontes
          </Button>

          {selectedSources.length > 0 && (
            <>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckIcon />}
                onClick={handleBulkValidate}
              >
                Validar ({selectedSources.length})
              </Button>
              <Button
                variant="outlined"
                startIcon={<AIIcon />}
                onClick={handleCategorizeWithAI}
              >
                Categorizar com IA
              </Button>
            </>
          )}
        </Box>
      </Card>

      {/* Tabela */}
      <Card>
        <TableContainer>
          {loading && <LinearProgress />}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selectedSources.length === sources.length && sources.length > 0}
                    indeterminate={selectedSources.length > 0 && selectedSources.length < sources.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Dominio</TableCell>
                <TableCell>Nome</TableCell>
                <TableCell>Plataforma</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Tier Tamanho</TableCell>
                <TableCell>Tier Segmento</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Solicitado por</TableCell>
                <TableCell align="right">Acoes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sources.map((source) => (
                <TableRow key={source._id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedSources.includes(source._id)}
                      onChange={() => handleSelectSource(source._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DomainIcon fontSize="small" color="action" />
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {source.domain}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {getDisplaySourceName(source)}
                      </Typography>
                      {source.siteName && !isHandleLike(source.siteName) && getDisplaySourceName(source) !== source.siteName && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          {source.siteName}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Chip
                        size="small"
                        label={PLATFORM_LABELS[source.platform] || source.platform || 'Website'}
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem', textTransform: 'capitalize', width: 'fit-content' }}
                      />
                      {source.username && source.platform && source.platform !== 'website' && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                          @{source.username}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {source.categories && source.categories.length > 0 ? (
                        source.categories.map((cat, idx) => (
                          <Chip
                            key={idx}
                            size="small"
                            label={cat}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))
                      ) : '-'}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={TIER_LABELS[source.sizeTier || source.defaultTier]}
                      color={TIER_COLORS[source.sizeTier || source.defaultTier]}
                    />
                  </TableCell>
                  <TableCell>
                    {source.segmentTier ? (
                      <Chip
                        size="small"
                        label={TIER_LABELS[source.segmentTier]}
                        color={TIER_COLORS[source.segmentTier]}
                        variant="outlined"
                        sx={{ borderStyle: 'dashed' }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.disabled">Não def.</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={STATUS_LABELS[source.status]}
                      color={STATUS_COLORS[source.status]}
                    />
                  </TableCell>
                  <TableCell>
                    {source.requestedBy?.length > 0 ? (
                      <Tooltip title={source.requestedBy.map(r => `${r.userEmail} (${r.brandName})`).join(', ')}>
                        <Typography variant="body2">
                          {source.requestedBy[0]?.brandName || source.requestedBy[0]?.userEmail || '-'}
                          {source.requestedBy.length > 1 && ` +${source.requestedBy.length - 1}`}
                        </Typography>
                      </Tooltip>
                    ) : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      {source.status === 'pending' ? (
                        <>
                          <Tooltip title="Validar">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenValidateDialog(source)}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Rejeitar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectSource(source)}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenValidateDialog(source)} 
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {sources.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      Nenhuma fonte encontrada
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Dialog Criar Fonte (Padronizado) */}
      <PlatformDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title="Nova Fonte Global"
          subtitle="Adicione uma fonte à base de dados compartilhada."
          icon={<AddIcon />}
          onClose={() => setDialogOpen(false)}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Domínio"
              value={formData.domain}
              onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
              placeholder="Ex: exemplo.com.br"
              fullWidth
              required
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Nome do Site"
              value={formData.siteName}
              onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
              placeholder="Ex: Jornal Exemplo"
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Se o nome vier como handle, ele será exibido de forma legível na tabela. Ex.: <strong>@contexto_exato</strong> vira <strong>Contexto Exato</strong>.
            </Typography>
            <FormControl fullWidth size="small">
              <InputLabel>Plataforma</InputLabel>
              <Select
                value={formData.platform || 'website'}
                label="Plataforma"
                onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                {Object.entries(PLATFORM_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Autocomplete
              multiple
              options={availableCategories.map(c => c.value)}
              value={formData.categories}
              onChange={(e, newValue) => setFormData({ ...formData, categories: newValue })}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Categorias" 
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="filled" label={option} size="small" {...getTagProps({ index })} key={index} 
                    sx={{ borderRadius: 1, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                ))
              }
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tier Tamanho</InputLabel>
                <Select
                  value={formData.sizeTier}
                  label="Tier Tamanho"
                  onChange={(e) => setFormData({ ...formData, sizeTier: e.target.value, defaultTier: e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={1}>Tier 1 - Grande (Nacional)</MenuItem>
                  <MenuItem value={2}>Tier 2 - Médio (Regional)</MenuItem>
                  <MenuItem value={3}>Tier 3 - Pequeno (Local)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Tier Segmento</InputLabel>
                <Select
                  value={formData.segmentTier || ''}
                  label="Tier Segmento"
                  onChange={(e) => setFormData({ ...formData, segmentTier: e.target.value === '' ? null : e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  <MenuItem value={1}>Tier 1 - Autoridade Nicho</MenuItem>
                  <MenuItem value={2}>Tier 2 - Relevante Nicho</MenuItem>
                  <MenuItem value={3}>Tier 3 - Comum Nicho</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Estimativa de Audiência / Seguidores"
              type="number"
              value={formData.subscribersEstimate}
              onChange={(e) => setFormData({ ...formData, subscribersEstimate: parseInt(e.target.value) || 0 })}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Notas para Moderação"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateSource}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
          >
            Criar Fonte Global
          </Button>
        </DialogActions>
      </PlatformDialog>

      {/* Dialog Validar/Editar Fonte (Padronizado) */}
      <PlatformDialog 
        open={validateDialogOpen} 
        onClose={() => setValidateDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <PlatformDialogTitle
          title={formData.status === 'pending' ? 'Validar Fonte Global' : 'Editar Fonte Global'}
          subtitle={`Domínio: ${formData.domain}`}
          icon={formData.status === 'pending' ? <ValidatedIcon /> : <EditIcon />}
          onClose={() => setValidateDialogOpen(false)}
          color={formData.status === 'pending' ? "success" : "primary"}
        />
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Nome do Site"
              value={formData.siteName}
              onChange={(e) => setFormData({ ...formData, siteName: e.target.value })}
              placeholder="Ex: Nome Oficial do Site"
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <Autocomplete
              multiple
              options={availableCategories.map(c => c.value)}
              value={formData.categories}
              onChange={(e, newValue) => setFormData({ ...formData, categories: newValue })}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label="Categorias" 
                  size="small"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip variant="filled" label={option} size="small" {...getTagProps({ index })} key={index} 
                    sx={{ borderRadius: 1, fontSize: '0.7rem', fontWeight: 600 }}
                  />
                ))
              }
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tier Tamanho</InputLabel>
                <Select
                  value={formData.sizeTier}
                  label="Tier Tamanho"
                  onChange={(e) => setFormData({ ...formData, sizeTier: e.target.value, defaultTier: e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={1}>Tier 1 - Grande (Nacional)</MenuItem>
                  <MenuItem value={2}>Tier 2 - Médio (Regional)</MenuItem>
                  <MenuItem value={3}>Tier 3 - Pequeno (Local)</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Tier Segmento</InputLabel>
                <Select
                  value={formData.segmentTier || ''}
                  label="Tier Segmento"
                  onChange={(e) => setFormData({ ...formData, segmentTier: e.target.value === '' ? null : e.target.value })}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  <MenuItem value={1}>Tier 1 - Autoridade Nicho</MenuItem>
                  <MenuItem value={2}>Tier 2 - Relevante Nicho</MenuItem>
                  <MenuItem value={3}>Tier 3 - Comum Nicho</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Estimativa de Audiência / Seguidores"
              type="number"
              value={formData.subscribersEstimate}
              onChange={(e) => setFormData({ ...formData, subscribersEstimate: parseInt(e.target.value) || 0 })}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              label="Notas e Justificativa"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={2}
              fullWidth
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button onClick={() => setValidateDialogOpen(false)} sx={{ fontWeight: 600 }}>Cancelar</Button>
          <Button 
            variant="contained" 
            color={formData.status === 'pending' ? "success" : "primary"} 
            onClick={handleValidateSource}
            sx={{ fontWeight: 700, borderRadius: 2, px: 3 }}
            startIcon={formData.status === 'pending' ? <CheckIcon /> : <RefreshIcon />}
          >
            {formData.status === 'pending' ? 'Confirmar Validação' : 'Salvar Alterações'}
          </Button>
        </DialogActions>
      </PlatformDialog>
    </Box>
  );
};

export default GlobalSourceManagement;
